import { formatPhoneNumber, formatPriceToFrenchSystem } from '@/utils/functions'
import { IPro } from '@/models/interfaces/interfaces';

/**
 * @fileoverview
 * This file uses interface merging to avoid redefining fields in the extended interface
 * To learn more about interface merging go to:
 * {@link https://www.typescriptlang.org/docs/handbook/declaration-merging.html}
 */

/**
 * Represents a topela professional
 * @class
 * @example
 */
export interface Pro extends IPro {}
export class Pro {
    /**
     * Create a topela professional
     * @constructor
     * @param proData 
     * @example
     * const pro = new Pro({ id: 1, company_name: 'My company'.. } : IPro)
     */
    constructor (proData: IPro) {
        Object.assign(this, proData)
    }

    /**
     * Returns the company reference in the format 'XXX XXX XXX XXXXX'
     */
    public get formated_company_reference (): string {
        if (!this.company_reference) return ''

        return this.company_reference.replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, '$1 $2 $3 $4')
    }

    /**
     * Returns the company reference in the format 'XXX XXX XXX RM XX'
     */
    public get formated_company_compl_reference (): string {
        if (!this.company_compl_reference) return ''

        return this.company_compl_reference.replace(/^(\d{3})(\d{3})(\d{3})([A-Z]+)(\d+)$/, '$1 $2 $3 $4 $5')
    }

    /**
     * Returns the phone number in the format 'XX XX XX XX XX'
     */
    public get formated_phone (): string {
        return formatPhoneNumber(this.phone)
    }

    /**
     * Returns the phone number in the format 'XX XX XX XX XX'
     */
    public get formated_company_phone () : string {
        return formatPhoneNumber(this.company_phone)
    }

    /**
     * Returns the up-to-date contact info of pro and not the archived info
     */
    public get formatted_contact_info () : { email: string, phone: string } {
        return {
            email: this.contact_info.email,
            phone: formatPhoneNumber(this.contact_info.phone)
        }
    }

    /**
     * Returns the iban formated with '\<br>' as line break
     */
    public get formated_iban(): string {
        if (!this.iban) return '';

        let formattedText = '';

        let index = 0;
        for (let i = 0; i < Math.min(this.iban.length, 27); i++) {
            if (i === 4 || i === 8 || i === 12 || i === 16 || i === 20 || i === 24) {
            formattedText += ' ';
            }
            formattedText += this.iban[index];
            index++;
        }
        return formattedText
    }

    /**
     * Returns the description formated with '\<br>' as line break
     */
    public get formated_description () : string {
        if (!this.description) {
            return ''
        }
        return this.description.replace(/\n/g, '<br>')
    }

    /**
     * Returns the full name of the professional
     */
    public get full_name (): string {
        let fullName: string

        if (this.first_name && this.last_name) {
            fullName = this.first_name + ' ' + this.last_name
        } else if (this.first_name && !this.last_name) {
            fullName = this.first_name
        } else if (!this.first_name && this.last_name) {
            fullName = this.last_name
        } else {
            fullName = this.username
        }

        return fullName
    }

    /**
     * Return company_share_capital in french currency format
     * @example 2 500,58
     */
    public get formated_company_share_capital(): string {
        if(!this.company_share_capital) return ""
        let result: string = formatPriceToFrenchSystem(this.company_share_capital)

        const last_part = result.split(",").pop()

        // if decimal is 0 return a whole number
        if (last_part === "00") {
            return result.replace(",00", "")
        }
        
        return result;
    }
}